import { useCallback, useEffect, useMemo, useRef } from "react";
import { useAppDispatch, useAppSelector } from "@/services/hooks";
import { getAllProducts } from "@/services/productServices/productActions";
import { getUsers } from "@/services/userServices/userActions";
import { getPosts } from "@/services/blogServices/blogActions";
import { getProductProperties } from "@/services/productPropertiesServices/ProductPropertiesActions";

// Stable fallback for the opt-in selectors below: a disabled flag selects this
// constant instead of the slice, so existing callers gain no new store
// subscriptions (and therefore no new re-renders) from the content sources.
const EMPTY: never[] = [];

/**
 * useVariableOptions — the one place that loads and shapes the option lists
 * the entity building blocks need (SCRUM-554; was duplicated as prepare*
 * helpers across TemplateForm, HandleRequired and HandleOptional).
 *
 * Fetch-if-empty on mount; sizes (not slice references) as effect deps so an
 * empty collection does not re-dispatch forever (SCRUM-571).
 *
 * Returns option lists keyed by variable TYPE (plus `users` for the
 * `reviewer` String special-case), each as react-select {label, value}
 * pairs with the FULL entity doc as value — the shape the legacy prepare*
 * helpers produced.
 *
 * Content sources (SCRUM-641, opt-in so the settings hosts stay untouched):
 *  - `productProperties: true` adds `productproperties` — the pages-side
 *    Product option list ({label: pp.product.title, value: pp doc}), mapped
 *    exactly like toProductOptions (useProductStateHost.js) / the update
 *    screens' prepareProducts. No page-type narrowing here: that stays a
 *    host concern (selectProductOptions, SCRUM-669).
 *  - `posts: true` adds `Post` — ACTIVE posts only, newest updatedAt first
 *    (createdAt fallback), the exact preparePosts semantics all three
 *    content screens share.
 * A disabled flag skips both the fetch and the store subscription.
 */
export type ContentSources = { productProperties?: boolean; posts?: boolean };

export function useVariableOptions({ productProperties = false, posts = false }: ContentSources = {}) {
  const dispatch = useAppDispatch();
  const products = useAppSelector((state) => state.products.products);
  // `?? EMPTY` (SCRUM-1024): the reducer now coerces the slice to an array, but
  // keep the consumer honest — `users` is read for .length and .map below, and a
  // null here white-screens the whole editor.
  const users = useAppSelector((state) => state.users.users ?? EMPTY);
  const productproperties = useAppSelector((state) =>
    productProperties ? state.productproperties.productproperties : EMPTY
  );
  const postList = useAppSelector((state) =>
    posts ? state.blog.posts : EMPTY
  );

  // The fetch-if-empty guards below test whether the STORE is empty, not whether
  // a request is already in flight. Every arriving response changes one of the
  // length deps, so the effect re-ran and re-dispatched every thunk whose data
  // had not landed yet — one /admin/template load fired /api/blog 5x,
  // /api/product 4x, /api/users 3x. Worse, a collection that is legitimately
  // empty (a site with no blog: blogCount stays 0 forever) can never satisfy its
  // guard, so it re-fired on every cascade step. Latch each thunk to at most one
  // dispatch per mount; the "store already has it -> do not fetch" behaviour is
  // unchanged. (SCRUM-950)
  const requested = useRef<Record<string, boolean>>({});
  const fetchOnce = useCallback(
    // `(...args: any[])` not `()`: the slices migrated to createAsyncThunk
    // (SCRUM-596) type their creators as taking an argument, even though every
    // call site here passes none.
    (key: string, thunk: (...args: any[]) => any) => { // any: mixed createAsyncThunk / plain-thunk creators, see above
      if (requested.current[key]) return;
      requested.current[key] = true;
      dispatch(thunk());
    },
    [dispatch]
  );

  useEffect(() => {
    if (products.length === 0) fetchOnce("products", getAllProducts);
    if (users.length === 0) fetchOnce("users", getUsers);
  }, [fetchOnce, products.length, users.length]);

  // Content-source fetches live in their own effect so a no-arg call is
  // behaviourally identical to before SCRUM-641 (same dispatches, same deps).
  useEffect(() => {
    if (productProperties && productproperties.length === 0)
      fetchOnce("productProperties", getProductProperties);
    if (posts && postList.length === 0) fetchOnce("posts", getPosts);
  }, [
    fetchOnce,
    productProperties,
    posts,
    productproperties.length,
    postList.length,
  ]);

  return useMemo(
    () => ({
      // No Category / Subcategory / Ad / Blog lists: no template declares a
      // variable of those types (SCRUM-2207), so none of them is fetched here
      // either (SCRUM-2328).
      Product: products.map((prod) => ({ label: prod.title, value: prod })),
      // the `reviewer` String special-case selects a user by NAME (value is
      // the name string, not the doc) — exactly like the legacy prepareUsers
      users: users.map((user) => ({ label: user.name, value: user.name })),
      // content sources (SCRUM-641) — EMPTY unless opted in via the flags
      productproperties: productproperties.map((pp) => ({
        label: pp.product.title,
        value: pp,
      })),
      Post: [...postList]
        .filter((post) => post.active)
        .sort(
          (a, b) =>
            new Date(b.updatedAt || b.createdAt || 0).getTime() -
            new Date(a.updatedAt || a.createdAt || 0).getTime()
        )
        .map((post) => ({ label: post.title, value: post })),
    }),
    [products, users, productproperties, postList]
  );
}
